/**
 * Bocas de acceso (paso 1.4): por dónde entra y sale el auto de cada estación, según la forma que
 * declara el catálogo, y hacia dónde conviene girar un elemento para que su boca mire a una vía.
 *
 * Es la única fuente de las bocas: el compilador las usa para las rutas y el plano las dibuja (verdes
 * si conectan con una vía, rojas si no). Así lo que se ve y lo que simula el motor no se separan.
 */

import type { BocaDeAcceso, Elemento, ItemDeCatalogo, Layout, Punto } from '../dominio/espacio';
import { puntoMasCercano, rayoAVia, tramosDe, type Tramo } from './circulacion';
import {
  aPlanta,
  distancia,
  frente,
  giroHacia,
  normalizarAngulo,
  proyectarEnSegmento,
} from './geometria';

/** Hasta dónde busca una boca una vía, desde el centro del elemento (como en el v6). */
export const ALCANCE_BOCA_M = 12;
/** Distancia máxima de un marcador (ingreso, salida) a una vía vehicular. */
export const MARCADOR_MAX_M = 6;
/** Vecinos del mismo tipo a esta distancia (entre centros) prestan su giro al colocar. */
export const VECINO_M = 8;

export interface BocaEnPlanta {
  /** Entrada (obligatoria) o salida propia (el cajón de recepción, si tiene una vía delante). */
  rol: 'entrada' | 'salida';
  /** Lado del elemento: detrás (−z local), delante (+z local) o el propio punto (marcador). */
  lado: 'detras' | 'delante' | 'centro';
  /** Punto del borde del elemento por donde pasa el auto (el centro, en un marcador). */
  borde: Punto;
  /** Dirección hacia afuera del elemento (unitaria). */
  direccion: Punto;
  /** Punto de la vía al que conecta; `null` = sin vía. */
  via: Punto | null;
}

/**
 * Bocas de un elemento con las vías de su circulación:
 *
 * - `detras` y `delante`: una boca en ese borde, conectada si una vía cruza el rayo que sale del
 *   centro hacia ese lado (hasta 12 m);
 * - `paso`: entra por detrás y, si hay una vía delante, sale por delante (si no, sale por donde
 *   entró);
 * - `marcador`: el punto se pega a la vía más cercana (hasta 6 m).
 */
export function bocasDeElemento(
  e: Pick<Elemento, 'posicion' | 'rotacion' | 'dimensiones'>,
  boca: BocaDeAcceso,
  tramos: readonly Tramo[],
): BocaEnPlanta[] {
  const f = frente(e.rotacion);
  if (boca.forma === 'marcador') {
    const cercano = puntoMasCercano(tramos, e.posicion);
    const via = cercano && cercano.d <= MARCADOR_MAX_M ? cercano.q : null;
    const d = via ? distancia(via, e.posicion) : 0;
    const direccion =
      via && d > 1e-6 ? { x: (via.x - e.posicion.x) / d, z: (via.z - e.posicion.z) / d } : f;
    return [{ rol: 'entrada', lado: 'centro', borde: e.posicion, direccion, via }];
  }
  const medio = e.dimensiones.fondo / 2;
  const atras = { x: -f.x, z: -f.z };
  const delante = boca.forma === 'delante';
  const entrada: BocaEnPlanta = {
    rol: 'entrada',
    lado: delante ? 'delante' : 'detras',
    borde: aPlanta(e, 0, delante ? medio : -medio),
    direccion: delante ? f : atras,
    via: rayoAVia(tramos, e.posicion, delante ? f : atras, ALCANCE_BOCA_M),
  };
  if (boca.forma !== 'paso') return [entrada];
  const salida = rayoAVia(tramos, e.posicion, f, ALCANCE_BOCA_M);
  return salida
    ? [
        entrada,
        { rol: 'salida', lado: 'delante', borde: aPlanta(e, 0, medio), direccion: f, via: salida },
      ]
    : [entrada];
}

/** Giro redondeado a grados enteros, para que el campo «Giro» muestre números limpios. */
function giroRedondo(r: number): number {
  return normalizarAngulo((Math.round((r * 180) / Math.PI) * Math.PI) / 180);
}

/**
 * Giro con el que la boca de entrada del elemento conecta con una vía, o `null` si ninguna vía
 * queda a su alcance en ninguna dirección. Prueba primero los giros `preferidos` (los de sus
 * vecinos: así una fila queda pareja) y después el que deja la boca más cerca de una vía, mirándola
 * de frente.
 */
export function giroHaciaVia(
  e: Pick<Elemento, 'posicion' | 'rotacion' | 'dimensiones'>,
  boca: BocaDeAcceso,
  tramos: readonly Tramo[],
  preferidos: readonly number[] = [],
): number | null {
  if (boca.forma === 'marcador' || tramos.length === 0) return null;
  const alcance = (rotacion: number): number | null => {
    const b = bocasDeElemento({ ...e, rotacion }, boca, tramos)[0]!;
    return b.via ? distancia(e.posicion, b.via) : null;
  };
  for (const r of preferidos) if (alcance(r) !== null) return normalizarAngulo(r);
  // Candidatos: la boca mirando de frente a cada tramo y los cuatro giros rectos.
  const candidatos: number[] = [];
  for (const t of tramos) {
    const pr = proyectarEnSegmento(e.posicion, t.a, t.b);
    if (pr.d < 0.3) continue;
    const u = { x: (pr.q.x - e.posicion.x) / pr.d, z: (pr.q.z - e.posicion.z) / pr.d };
    const giro = giroHacia(boca.forma === 'delante' ? u : { x: -u.x, z: -u.z });
    candidatos.push(giroRedondo(giro), giro);
  }
  candidatos.push(0, Math.PI / 2, Math.PI, -Math.PI / 2);
  let mejor: { r: number; d: number } | null = null;
  for (const r of candidatos) {
    const d = alcance(r);
    if (d !== null && (!mejor || d < mejor.d - 1e-6)) mejor = { r, d };
  }
  return mejor ? normalizarAngulo(mejor.r) : null;
}

/** Tramos de la circulación de una boca. */
export function tramosDeBoca(layout: Pick<Layout, 'vias'>, boca: BocaDeAcceso): Tramo[] {
  return tramosDe(layout.vias, boca.circulacion);
}

/**
 * Dónde y con qué giro queda un elemento al colocarlo (paso 1.4), sin girarlo a mano:
 *
 * - un marcador (ingreso, salida) se pega a la vía más cercana y se alinea con ella;
 * - un elemento que la vía atraviesa (portón, barrera, puerta) se alinea con la vía que lo cruza;
 * - un elemento con boca hereda el giro de un vecino del mismo tipo si con él conecta; si no, su
 *   boca mira a la vía más cercana;
 * - sin boca, hereda el giro de su vecino del mismo tipo, si lo hay.
 */
export function colocacionSugerida(
  e: Elemento,
  item: ItemDeCatalogo,
  layout: Pick<Layout, 'vias' | 'elementos'>,
): { posicion: Punto; rotacion: number } {
  const vecinos = girosDeVecinos(e, layout.elementos);
  const boca = item.boca;
  if (!boca) return { posicion: e.posicion, rotacion: vecinos[0] ?? e.rotacion };
  const tramos = tramosDeBoca(layout, boca);

  if (boca.forma === 'marcador') {
    const cercano = masCercano(tramos, e.posicion);
    if (!cercano || cercano.pr.d > MARCADOR_MAX_M)
      return { posicion: e.posicion, rotacion: e.rotacion };
    const { a, b } = cercano.tramo;
    const largo = distancia(a, b);
    return {
      posicion: { x: redondear(cercano.pr.q.x), z: redondear(cercano.pr.q.z) },
      rotacion: giroRedondo(giroHacia({ x: (b.x - a.x) / largo, z: (b.z - a.z) / largo })),
    };
  }

  if (boca.forma === 'paso' && !item.rolOperativo) {
    const cercano = masCercano(tramos, e.posicion);
    if (cercano && cercano.pr.d <= cercano.tramo.via.ancho / 2) {
      const { a, b } = cercano.tramo;
      const largo = distancia(a, b);
      return {
        posicion: e.posicion,
        rotacion: giroRedondo(giroHacia({ x: (b.x - a.x) / largo, z: (b.z - a.z) / largo })),
      };
    }
    return { posicion: e.posicion, rotacion: vecinos[0] ?? e.rotacion };
  }

  const giro = giroHaciaVia(e, boca, tramos, vecinos);
  return { posicion: e.posicion, rotacion: giro ?? vecinos[0] ?? e.rotacion };
}

/** Giros de los vecinos del mismo tipo (a menos de 8 m), del más cercano al más lejano. */
export function girosDeVecinos(
  e: Pick<Elemento, 'id' | 'catalogoId' | 'posicion'>,
  elementos: readonly Elemento[],
): number[] {
  return elementos
    .filter(
      (x) =>
        x.id !== e.id &&
        x.catalogoId === e.catalogoId &&
        distancia(x.posicion, e.posicion) <= VECINO_M,
    )
    .sort((a, b) => distancia(a.posicion, e.posicion) - distancia(b.posicion, e.posicion))
    .map((x) => x.rotacion);
}

function masCercano(tramos: readonly Tramo[], pt: Punto) {
  let mejor: { tramo: Tramo; pr: ReturnType<typeof proyectarEnSegmento> } | null = null;
  for (const t of tramos) {
    if (distancia(t.a, t.b) < 1e-6) continue;
    const pr = proyectarEnSegmento(pt, t.a, t.b);
    if (!mejor || pr.d < mejor.pr.d) mejor = { tramo: t, pr };
  }
  return mejor;
}

const redondear = (v: number) => Math.round(v * 100) / 100;
